// Native Capacitor plugin initialisation.
// All imports are dynamic so this file tree-shakes cleanly in the web build —
// @capacitor/core is a peer dep that resolves to a no-op stub in the browser.

import { Capacitor } from '@capacitor/core';
import { API_BASE } from './apiBase';
import type { URLOpenListenerEvent, AppState } from '@capacitor/app';
import type {
  Token,
  RegistrationError,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function nativePlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

export async function initNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const [
    { SplashScreen },
    { StatusBar, Style },
    { App },
    { Haptics, ImpactStyle },
    { Keyboard },
  ] = await Promise.all([
    import('@capacitor/splash-screen'),
    import('@capacitor/status-bar'),
    import('@capacitor/app'),
    import('@capacitor/haptics'),
    import('@capacitor/keyboard'),
  ]);

  // ── Status bar ──────────────────────────────────────────────────────────
  await StatusBar.setStyle({ style: Style.Dark });
  await StatusBar.setBackgroundColor({ color: '#1D4ED8' });

  // ── Keyboard ─────────────────────────────────────────────────────────────
  Keyboard.addListener('keyboardWillShow', () => {
    document.documentElement.classList.add('keyboard-open');
  });
  Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.classList.remove('keyboard-open');
  });

  // ── Deep links / Universal Links ─────────────────────────────────────────
  // When the OS opens a housewizrd:// link or an https://housewizrd.com/* link
  // while the app is running, push the path into the React Router history.
  App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    try {
      const url = new URL(event.url);
      const path = url.pathname + url.search + url.hash;
      if (path && path !== window.location.pathname) {
        // Push into browser history so React Router picks it up
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch {
      // Malformed URL — ignore
    }
  });

  // ── App lifecycle ────────────────────────────────────────────────────────
  App.addListener('appStateChange', ({ isActive }: AppState) => {
    if (isActive) {
      // Refresh auth token when app resumes from background
      window.dispatchEvent(new CustomEvent('dwelfix:app-resume'));
    }
  });

  // ── Push notifications ───────────────────────────────────────────────────
  await registerNativePush();

  // ── Haptic helper (exported for use in components) ────────────────────────
  window.__dwelfixHaptic = async (style: 'light' | 'medium' | 'heavy' = 'light') => {
    const map = {
      light:  ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy:  ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: map[style] });
  };

  // ── Hide splash AFTER the React tree is painted ──────────────────────────
  // App.tsx calls this via hideSplash() once auth state is resolved.
  window.__dwelfixHideSplash = () => SplashScreen.hide({ fadeOutDuration: 250 });
}

async function registerNativePush(): Promise<void> {
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let permResult = await PushNotifications.checkPermissions();

  if (permResult.receive === 'prompt') {
    permResult = await PushNotifications.requestPermissions();
  }

  if (permResult.receive !== 'granted') return;

  await PushNotifications.register();

  // Send the APNs / FCM token to the server so we can deliver push later
  PushNotifications.addListener('registration', async (token: Token) => {
    try {
      await fetch(`${API_BASE}/notifications/native-token`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token.value,
          platform: Capacitor.getPlatform(),
        }),
      });
    } catch {
      // Non-fatal — push still works at OS level, we just can't server-push
    }
  });

  PushNotifications.addListener('registrationError', (err: RegistrationError) => {
    console.warn('Push registration error:', err.error);
  });

  // Foreground notification received — the OS shows it for us (badge/sound/alert)
  PushNotifications.addListener('pushNotificationReceived', (_notification: PushNotificationSchema) => {
    // Optionally show an in-app toast here
  });

  // User tapped a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    const data = action.notification.data as { url?: string } | undefined;
    if (data?.url) {
      try {
        const path = new URL(data.url).pathname;
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch {
        // Bad URL in payload
      }
    }
  });
}

// Globals so App.tsx doesn't need to import @capacitor/* at the top level
declare global {
  interface Window {
    __dwelfixHaptic?: (style?: 'light' | 'medium' | 'heavy') => Promise<void>;
    __dwelfixHideSplash?: () => void;
  }
}
